/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { CSSTransitionGroup } from 'react-transition-group-v1';

import type { XdpV1_Org_Coursera_Xdp_Sdp_SdpCourse as Course } from '__generated__/graphql-types-do-not-use';

import logger from 'js/app/loggerSingleton';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import requestCountry from 'js/lib/requestCountry';

import type { CSSProperties } from '@coursera/coursera-ui';
import { SvgButton, breakPoint } from '@coursera/coursera-ui';
import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import MixAndMatchS12nContextMessage from 'bundles/enroll-modal/components/common/MixAndMatchS12nContextMessage';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import S12nNonRecurringChoiceFooter from 'bundles/enroll-modal/components/s12n-non-recurring/S12nNonRecurringChoiceFooter';
import S12nNonRecurringChoices from 'bundles/enroll-modal/components/s12n-non-recurring/S12nNonRecurringChoices';
import S12nPaymentMethod from 'bundles/enroll-modal/components/s12n-non-recurring/S12nPaymentMethod';
import { NON_RECURRING_CHOICES } from 'bundles/enroll-modal/components/s12n-non-recurring/utils/constants';
import { withS12nProductInfo } from 'bundles/enroll-modal/components/s12n-non-recurring/withS12nProductInfo';
import type { PropsFromWithS12nProductInfo } from 'bundles/enroll-modal/components/s12n-non-recurring/withS12nProductInfo';
import S12nSubscriptionFooter from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionFooter';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import {
  getHoursPerWeekFromCourses,
  getTotalMonthsFromCourses,
} from 'bundles/enterprise-legacy-learner-home/utils/xdpUtils';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { PrepaidProduct } from 'bundles/ownable-products-common/types/ProductInfoTypes';
import { getS12nMonthlySkuId } from 'bundles/ownable-products-common/utils/subscriptionPriceUtils';
import { freeTrial } from 'bundles/payments-common/common/constants';
import { getShortS12nFreeTrialOverrideDays } from 'bundles/payments-common/utils/s12nFreeTrialEpicUtils';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/s12n-enroll';

const MODAL_TYPES = {
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  NON_RECURRING_CHOICES: 'NON_RECURRING_CHOICES',
  ERROR: 'ERROR',
};

type PropsFromCaller = {
  s12nId: string;
  onSdp: boolean;
  courseId?: string;
  isFromS12nSelection?: boolean;
  onClose?: () => void;
  didSkipMixAndMatchModal?: boolean;
};

type PropsToComponent = PropsFromCaller & PropsFromWithS12nProductInfo;

const styles: Record<string, CSSProperties> = {
  s12nEnrollModal: {
    '.c-modal-content': {
      padding: '0 !important',
      [`@media (max-width: 767px)`]: {
        maxWidth: '100% !important',
        width: '100% !important',
        top: '0 !important',
      },
    },
    '.c-modal-x-out a': {
      color: 'var(--cds-color-grey-25) !important',
    },
  },
  titleContainer: {
    padding: '40px 32px 32px',
    background: 'var(--cds-color-blue-950)',
  },
  title: {
    fontFamily: 'OpenSans',
    color: 'var(--cds-color-neutral-primary-invert)',
    fontSize: '24px',
    lineHeight: '30px',
    textAlign: 'center',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      textAlign: 'left',
    },
  },
  modalContent: {
    padding: '30px 0 0 0',
  },
  paymentMethodContent: {
    padding: '0 50px 30px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '0 30px 30px',
    },
  },
  paymentTitle: {
    fontFamily: 'OpenSans',
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '24px',
    marginBottom: '18px',
  },
  valuePropsTitle: {
    display: 'block',
    fontSize: '14px',
    marginBottom: '8px',
  },
  valueProps: {
    paddingLeft: '20px',
    marginBottom: '25px',
  },
  valueProp: {
    marginBottom: '8px',
  },
  paymentMethodContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      flexDirection: 'column',

      ':nth-of-type(n) > div:first-of-type': {
        marginBottom: '40px',
      },
    },
    '.rc-PromotionApplicableCheckoutMessage': {
      lineHeight: '21px !important',
      marginBottom: '16px',
    },
  },
  gradientFooter: {
    height: '80px',
    position: 'sticky',
    width: '100%',
    bottom: 0,
    left: 0,
    background: 'linear-gradient(transparent,var(--cds-color-neutral-background-primary) 70%)',
    [`@media (min-width: ${breakPoint.md}px)`]: {
      display: 'none',
    },
  },
  goBackButton: {
    padding: '0 50px',
    minHeight: '24px',
    marginBottom: '25px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '0 30px',
    },
  },
  auditFooter: {
    width: '300px',
    margin: '16px auto 0',
  },
  stickyFooter: {
    position: 'sticky',
    bottom: 0,
  },
};

const handleClose = (onClose?: () => void) => {
  if (onClose) {
    onClose();
  }
};

const handleContinue = (
  setIsEnrolling: (isEnrolling: boolean) => void,
  setEnrollmentError: (error: ApiError | undefined) => void,
  setActiveModal: (modalType: string) => void,
  enrollmentAvailableChoices: EnrollmentChoices,
  enrollmentChoice: keyof typeof EnrollmentChoiceTypes,
  s12nId: string,
  isFirstNonRecurringChoiceSelected?: boolean,
  courseId?: string,
  additionalData?: { paymentPassOption: PrepaidProduct },
  promoCode?: string
) => {
  setIsEnrolling(true);
  setEnrollmentError(undefined);

  const data = {
    s12nId,
    ...additionalData,
  };

  const productSkuId = getS12nMonthlySkuId(enrollmentAvailableChoices, s12nId);

  // Promotion only applies to s12n subscription checkout and is only passed in that `handleContinue`
  // TODO - update once we support promotions for s12n prepaid

  const options = {
    productSkuId,
    courseId,
    s12nId,
    data,
  };

  // @ts-expect-error ts-migrate(7015) FIXME: Element implicitly has an 'any' type because index... Remove this comment to see the full error message
  const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoice];
  submitEnrollmentPromise({
    handleSubmitPromise,
    options,
    promoCode:
      (enrollmentChoice === 'ENROLL_THROUGH_S12N_PREPAID' && isFirstNonRecurringChoiceSelected) ||
      enrollmentChoice !== 'ENROLL_THROUGH_S12N_PREPAID'
        ? promoCode
        : undefined,
  }).catch((error: ApiError) => {
    setEnrollmentError(error);
    setActiveModal(MODAL_TYPES.ERROR);
  });
};

const renderModalBody = (
  onSdp: boolean,
  numOfCourses: number,
  product: string,
  productName: string,
  s12nName: string,
  partnerName: string,
  didSkipMixAndMatchModal: boolean
) => {
  let contextLine: React.ReactNode;
  if (didSkipMixAndMatchModal) {
    contextLine = <MixAndMatchS12nContextMessage />;
  } else if (onSdp) {
    contextLine = _t('#{productName} is a #{numOfCourses}-course #{product}.', { productName, numOfCourses, product });
  } else {
    contextLine = _t('#{productName} is part of the #{s12nName} certificate.', { productName, s12nName });
  }

  return (
    <React.Fragment>
      <p>{contextLine}</p>

      <div>
        <strong css={styles.valuePropsTitle}>{_t('This #{product} includes:', { product })}</strong>
        <ul css={styles.valueProps}>
          <li>
            <p css={styles.valueProp}>{_t('Unlimited access to all #{numOfCourses} courses', { numOfCourses })}</p>
          </li>
          <li>
            <p>{_t('Shareable certificate of completion from #{partnerName}', { partnerName })}</p>
          </li>
        </ul>
      </div>
    </React.Fragment>
  );
};

const renderModalContent = (
  choiceType: string,
  setChoiceType: (choice: string) => void,
  activeModal: string,
  setActiveModal: React.Dispatch<React.SetStateAction<string>>,
  isEnrolling: boolean,
  setIsEnrolling: React.Dispatch<React.SetStateAction<boolean>>,
  setEnrollmentError: React.Dispatch<React.SetStateAction<ApiError | undefined>>,
  enrollmentAvailableChoices: EnrollmentChoices,
  onSdp: boolean,
  numOfCourses: number,
  courses: Array<Course>,
  product: string,
  productName: string,
  s12nName: string,
  partnerName: string,
  prepaidPriceText: JSX.Element | null,
  subscriptionPriceText: JSX.Element,
  hasFreeTrial: boolean,
  didSkipMixAndMatchModal: boolean,
  s12nId: string,
  courseId?: string,
  promotionEligibilities?: PromotionEligibilitiesV1 | null
) => {
  const numOfHoursPerWeek = getHoursPerWeekFromCourses(courses);
  const numOfMonths = getTotalMonthsFromCourses(courses);
  const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities.promoCodeId : undefined;
  const numDays = getShortS12nFreeTrialOverrideDays(s12nId) ?? freeTrial.numDays;

  const nonRecurringValueProps =
    requestCountry.get() === 'IN' ? [_t('EMI Available'), _t('7 day refund period')] : [_t('7 day refund period')];
  switch (activeModal) {
    case MODAL_TYPES.PAYMENT_METHOD: {
      if (!prepaidPriceText) {
        return <LoadingIcon />;
      }

      return (
        <React.Fragment>
          <div css={styles.paymentMethodContent}>
            {renderModalBody(onSdp, numOfCourses, product, productName, s12nName, partnerName, didSkipMixAndMatchModal)}
            <h3 css={styles.paymentTitle}>{_t('Choose your enrollment method')}</h3>
            <div css={styles.paymentMethodContainer}>
              <S12nPaymentMethod
                dataTestid="NonRecurringPayment"
                title={_t('One-time payment')}
                description={
                  <FormattedMessage
                    message={_t('Plans starting from {priceText}. Does not automatically renew.')}
                    priceText={prepaidPriceText}
                  />
                }
                valueProps={nonRecurringValueProps}
                onButtonClick={() => setActiveModal(MODAL_TYPES.NON_RECURRING_CHOICES)}
                isEnrolling={isEnrolling}
                hideLoader={true}
              />
              <S12nPaymentMethod
                dataTestid="SubscriptionPayment"
                title={_t('Monthly Subscription')}
                description={
                  <FormattedMessage
                    message={_t('Pay {priceText} per month, charged automatically every month.')}
                    priceText={subscriptionPriceText}
                  />
                }
                valueProps={[
                  ...(hasFreeTrial && !promotionEligibilities?.isEligible
                    ? [_t('#{numDays}-day free trial', { numDays })]
                    : []),
                  _t('Cancel anytime'),
                ]}
                onButtonClick={() =>
                  handleContinue(
                    setIsEnrolling,
                    setEnrollmentError,
                    setActiveModal,
                    enrollmentAvailableChoices,
                    EnrollmentChoiceTypes.ENROLL_THROUGH_S12N_SUBSCRIPTION_TRIAL,
                    s12nId,
                    false,
                    courseId,
                    undefined,
                    promoCode
                  )
                }
                additionalMessage={<PromotionApplicableCheckoutMessage s12nId={s12nId} />}
                isEnrolling={isEnrolling}
              />
            </div>
            {!onSdp && <S12nSubscriptionFooter />}
          </div>
          <div css={styles.gradientFooter} />
        </React.Fragment>
      );
    }
    case MODAL_TYPES.NON_RECURRING_CHOICES:
      return (
        <React.Fragment>
          <SvgButton
            type="icon"
            svgElement={
              <SvgArrowLeft
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            }
            onClick={() => setActiveModal(MODAL_TYPES.PAYMENT_METHOD)}
            css={styles.goBackButton}
          />
          <S12nNonRecurringChoices
            numOfCourses={numOfCourses}
            numOfHoursPerWeek={numOfHoursPerWeek}
            numOfMonths={numOfMonths}
            choiceType={choiceType}
            setChoiceType={setChoiceType}
            s12nId={s12nId}
          />
          <div css={styles.stickyFooter}>
            <S12nNonRecurringChoiceFooter
              choiceType={choiceType}
              onButtonClick={(paymentPassOption: PrepaidProduct) =>
                handleContinue(
                  setIsEnrolling,
                  setEnrollmentError,
                  setActiveModal,
                  enrollmentAvailableChoices,
                  EnrollmentChoiceTypes.ENROLL_THROUGH_S12N_PREPAID,
                  s12nId,
                  choiceType === NON_RECURRING_CHOICES.PASS_ONE,
                  courseId,
                  {
                    paymentPassOption,
                  },
                  promoCode
                )
              }
              isEnrolling={isEnrolling}
            />
          </div>
        </React.Fragment>
      );
    case MODAL_TYPES.ERROR:
    default:
      logger.error('Unable to render enroll modal content');
      return null;
  }
};

export const S12nPaymentMethodEnrollModal = ({
  s12nName,
  s12nId,
  courseId,
  onClose,
  productName,
  product,
  numOfCourses,
  courses,
  partnerName,
  onSdp,
  didSkipMixAndMatchModal = false,
}: PropsToComponent) => {
  const {
    enrollmentAvailableChoices,
    promotionEligibilities,
    s12nDerivatives,
    s12nSubscriptionOwnableProduct,
    s12nPrepaidProductPrice,
    isSpecialization,
  } = usePageData();

  const [activeModal, setActiveModal] = useState(MODAL_TYPES.PAYMENT_METHOD);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<ApiError | undefined>(undefined);
  const [choiceType, setChoiceType] = useState(NON_RECURRING_CHOICES.PASS_TWO);

  const hasFreeTrial = enrollmentAvailableChoices?.canEnrollThroughS12nSubscriptionFreeTrial ?? false;
  const prepaidProducts = enrollmentAvailableChoices?.getS12nPrepaidProducts();

  const s12nSubscriptionPrice = s12nSubscriptionOwnableProduct?.price ?? s12nDerivatives?.catalogPrice;
  if (!prepaidProducts || !enrollmentAvailableChoices || !s12nSubscriptionPrice) {
    logger.error('Unable to render enroll modal content');
    return null;
  }

  const prepaidPriceText = s12nPrepaidProductPrice ? (
    <ReactPriceDisplay value={s12nPrepaidProductPrice.amount} currency={s12nPrepaidProductPrice.currencyCode} />
  ) : null;
  const subscriptionPriceText = (
    <ReactPriceDisplay value={s12nSubscriptionPrice.amount} currency={s12nSubscriptionPrice.currencyCode} />
  );

  if (activeModal === MODAL_TYPES.ERROR) {
    return (
      <EnrollErrorModal
        error={enrollmentError}
        onClose={() => handleClose(onClose)}
        isFinancialAid={false}
        s12nId={s12nId}
        courseId={courseId}
        isSpecialization={isSpecialization}
      />
    );
  } else {
    return (
      <div data-testid="rc-S12nPaymentMethodEnrollModal" css={styles.s12nEnrollModal}>
        <CSSTransitionGroup transitionName="fade" transitionEnter={false} transitionLeaveTimeout={300}>
          <Modal
            trackingName="s12n_payment_method_enroll_modal"
            data={{ id: s12nId }}
            key="S12nPaymentMethodEnrollModal"
            modalName={_t('Join this #{product}', { product })}
            handleClose={() => handleClose(onClose)}
            allowClose={true}
          >
            <div css={styles.titleContainer}>
              <h2 css={styles.title}>
                {_t('Enroll in this #{numOfCourses}-course #{product}', { numOfCourses, product })}
              </h2>
            </div>
            <div css={styles.modalContent}>
              {renderModalContent(
                choiceType,
                setChoiceType,
                activeModal,
                setActiveModal,
                isEnrolling,
                setIsEnrolling,
                setEnrollmentError,
                enrollmentAvailableChoices,
                onSdp,
                numOfCourses,
                courses,
                product,
                productName,
                s12nName,
                partnerName,
                prepaidPriceText,
                subscriptionPriceText,
                hasFreeTrial,
                didSkipMixAndMatchModal,
                s12nId,
                courseId,
                promotionEligibilities
              )}
            </div>
          </Modal>
        </CSSTransitionGroup>
      </div>
    );
  }
};

export default withS12nProductInfo<PropsFromCaller>()(S12nPaymentMethodEnrollModal);
